import type { IncidentOption } from "@no-busy-work/ops-bridge-protocol";
import {
  APP_ENVIRONMENT_OPTIONS,
  APP_OPTIONS,
  AUDIENCE_OPTIONS,
  CUSTOM_SERVICE_OPTION,
  LOCALE_OPTIONS,
  OWNER_OPTIONS,
  SERVICE_OPTIONS_BY_APP,
  SEVERITY_OPTIONS,
  SOURCE_OPTIONS
} from "./OpsCenter.Config";
import type { HealthResponse, OpsCenterCopy } from "./OpsCenter.Types";

export type CatalogOptionTuple = readonly [string, string];
type CatalogOptionMap = Readonly<Record<string, readonly CatalogOptionTuple[]>>;

export interface IncidentCreateCatalog {
  readonly apps: readonly CatalogOptionTuple[];
  readonly environmentsByApp: CatalogOptionMap;
  readonly servicesByApp: CatalogOptionMap;
  readonly sources: readonly CatalogOptionTuple[];
  readonly locales: readonly CatalogOptionTuple[];
  readonly audiences: readonly CatalogOptionTuple[];
  readonly owners: readonly CatalogOptionTuple[];
  readonly severities: readonly CatalogOptionTuple[];
}

export function incidentCreateCatalog(health: HealthResponse | null): IncidentCreateCatalog {
  const options = health?.incidentCreateOptions;
  return {
    apps: optionRowsOrFallback(options?.apps, APP_OPTIONS),
    environmentsByApp: optionMapOrFallback(options?.environmentsByApp, APP_ENVIRONMENT_OPTIONS),
    servicesByApp: serviceOptionMapOrFallback(options?.servicesByApp),
    sources: optionRowsOrFallback(options?.sources, SOURCE_OPTIONS),
    locales: optionRowsOrFallback(options?.locales, LOCALE_OPTIONS),
    audiences: optionRowsOrFallback(options?.audiences, AUDIENCE_OPTIONS),
    owners: optionRowsOrFallback(options?.owners, OWNER_OPTIONS),
    severities: optionRowsOrFallback(options?.severities, SEVERITY_OPTIONS)
  };
}

export function selectedCatalogAppId(appId: string, catalog: IncidentCreateCatalog): string {
  return hasOption(catalog.apps, appId) ? appId : catalog.apps[0]?.[0] ?? "nbw";
}

export function optionsForApp(optionsByApp: CatalogOptionMap, appId: string): readonly CatalogOptionTuple[] {
  return optionsByApp[appId] ?? optionsByApp.nbw ?? Object.values(optionsByApp)[0] ?? [];
}

export function ownerDisplayOptions(options: readonly CatalogOptionTuple[], copy: OpsCenterCopy): readonly CatalogOptionTuple[] {
  return options.map(([value, label]) => [value, `${ownerTypeLabel(value, copy)}: ${label}`] as const);
}

export function ownerTypeLabel(value: string, copy: OpsCenterCopy): string {
  if (value.startsWith("employee:")) {
    return copy.status.human;
  }
  if (value.startsWith("ai:")) {
    return copy.status.ai;
  }
  if (value.startsWith("system:")) {
    return copy.status.system;
  }
  if (value.startsWith("vendor:")) {
    return copy.status.vendor;
  }
  if (value.startsWith("code-bridge:")) {
    return copy.sections.codeBridge;
  }
  if (value.startsWith("owner:")) {
    return copy.labels.ownerTeam;
  }
  return copy.labels.owner;
}

function serviceOptionMapOrFallback(optionsByApp: Readonly<Record<string, readonly IncidentOption[]>> | undefined): CatalogOptionMap {
  const mapped = optionMapOrFallback(optionsByApp, SERVICE_OPTIONS_BY_APP);
  return Object.fromEntries(
    Object.entries(mapped).map(([appId, options]) => [appId, ensureCustomServiceOption(appId, options)])
  );
}

function ensureCustomServiceOption(appId: string, options: readonly CatalogOptionTuple[]): readonly CatalogOptionTuple[] {
  const fallbackCustomOption = optionsForApp(SERVICE_OPTIONS_BY_APP, appId).find(([value]) => value === CUSTOM_SERVICE_OPTION);
  return hasOption(options, CUSTOM_SERVICE_OPTION) || !fallbackCustomOption ? options : [...options, fallbackCustomOption];
}

function optionRowsOrFallback(options: readonly IncidentOption[] | undefined, fallback: readonly CatalogOptionTuple[]): readonly CatalogOptionTuple[] {
  return options?.length ? options.map((option) => [option.value, option.label] as const) : fallback;
}

function optionMapOrFallback(
  optionsByApp: Readonly<Record<string, readonly IncidentOption[]>> | undefined,
  fallback: CatalogOptionMap
): CatalogOptionMap {
  if (!optionsByApp || Object.values(optionsByApp).every((options) => options.length === 0)) {
    return fallback;
  }
  return {
    ...fallback,
    ...Object.fromEntries(
      Object.entries(optionsByApp)
        .filter(([, options]) => options.length > 0)
        .map(([appId, options]) => [appId, options.map((option) => [option.value, option.label] as const)])
    )
  };
}

function hasOption(options: readonly CatalogOptionTuple[], value: string): boolean {
  return options.some(([optionValue]) => optionValue === value);
}
